import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Radio,
  CircularProgress,
  Stack,
  Paper,
  useTheme,
  IconButton,
  styled,
} from "@mui/material";
import Button from "../../../shared/button/Button";
import { useNavigate } from "react-router-dom";
import API from "../../../api/Axios";
import { getRoleData } from "../../../shared/functions/commonfunction";
import waterTap from "@/assets/images/icons/waterTap.svg";
import Toilet from "@/assets/images/icons/dashboardToiletImage.svg";
import HandWash from "@/assets/images/icons/dashboardHandwashImage.svg";
import OperationAndMaintenance from "@/assets/images/icons/dashboardOperationAndMaintanceImage.svg";
import BehaviourChanege from "@/assets/images/icons/dashboardBehaviorChangeImage.svg";
import EcoFriendly from "@/assets/images/icons/dashboardEcoFriendlyImage.svg";
import DataLoader from "../../../shared/DataLoader/DataLoader";
import ImagePreview from "../../../shared/preview/ImagePreview";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useTranslation } from "react-i18next";

const iconMap = {
  Water: waterTap,
  Toilet: Toilet,
  "Handwashing with Soap": HandWash,
  "Operation and Maintenance": OperationAndMaintenance,
  "Behavior Change and Capacity Building": BehaviourChanege,
  "Mission LiFE activities": EcoFriendly,
};

function getFilteredQuestions(
  categoryQuestions: any[],
  notForSchoolTypes: any,
  schoolCategory: any
) {
  let filteredQuestions = [...categoryQuestions];
  if (
    notForSchoolTypes &&
    schoolCategory &&
    notForSchoolTypes[schoolCategory]
  ) {
    const notForSchoolTypeConfig = notForSchoolTypes[schoolCategory];
    // Remove questions by questionIds
    // if (
    //   notForSchoolTypeConfig.questionIds &&
    //   notForSchoolTypeConfig.questionIds.length > 0
    // ) {
    //   filteredQuestions = filteredQuestions.filter(
    //     (question) =>
    //       !notForSchoolTypeConfig.questionIds.includes(question.questionId)
    //   );
    // }
    // Remove options by questionOptions
    if (
      notForSchoolTypeConfig.questionOptions &&
      notForSchoolTypeConfig.questionOptions.length > 0
    ) {
      filteredQuestions = filteredQuestions.map((question) => {
        const removeOptions = notForSchoolTypeConfig.questionOptions.filter(
          (optionConfig) => optionConfig.questionId === question.questionId
        );
        if (removeOptions.length > 0 && question.options) {
          const removeOptionIds = removeOptions.map(
            (optionConfig) => optionConfig.optionId
          );
          return {
            ...question,
            options: question.options.filter(
              (option: any) => !removeOptionIds.includes(option.optionId)
            ),
          };
        }
        return question;
      });
    }
  }
  return filteredQuestions;
}

function getQuestionNumber(
  categories: any[],
  notForSchoolTypes: any,
  schoolCategory: any,
  categoryId: number,
  filteredIndex: number
) {
  let totalQuestions = 0;
  for (let i = 0; i < categories.length; i++) {
    const category = categories[i];
    if (category.categoryId === categoryId) {
      break;
    }
    const filteredQuestions = getFilteredQuestions(
      category.questions || [],
      notForSchoolTypes,
      schoolCategory
    );
    totalQuestions += filteredQuestions.length;
  }
  return totalQuestions + filteredIndex + 1;
}

function getAnswerLabel(question: any, answer: any) {
  if (!question || answer === undefined || answer === null) return "";
  if (question.questionType === "single-select") {
    const option = question.options?.find(
      (opt: any) => String(opt.optionId) === String(answer)
    );
    return option?.label || "";
  }
  if (question.questionType === "input") {
    return answer == 0 || answer ? answer : "";
  }
  if (question.questionType === "multi_input") {
    if (!answer || typeof answer !== "object") return "";
    return Object.entries(answer)
      .filter(([_, value]) => value !== null && value !== "")
      .map(([optionId, value]) => {
        const option = question.options?.find(
          (opt: any) => String(opt.optionId) === String(optionId)
        );
        return option ? `${option.label}: ${value}` : value;
      })
      .join(", ");
  }
  return "";
}

// Add getQuestionDisplayNumbers utility
function getQuestionDisplayNumbers(allQuestions: any[]) {
  const displayNumbers: Record<number, string> = {};
  let mainNumber = 1;
  for (let i = 0; i < allQuestions.length; i++) {
    const q = allQuestions[i];
    if (q.static_question_number && q.display_question_number) {
      displayNumbers[q.questionId] = q.display_question_number;
    } else {
      displayNumbers[q.questionId] = `${mainNumber}`;
      mainNumber++;
    }
  }
  return displayNumbers;
}

const QuestionPreview = () => {
  const { t } = useTranslation("dashboard");
  const [allQuestionsData, setAllQuestionsData] = useState<any>(null);
  const [allAnswersData, setAllAnswersData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState<any[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewCatIdx, setPreviewCatIdx] = useState<number | null>(null);
  const [previewIdx, setPreviewIdx] = useState(0);
  const [loadingPreviews, setLoadingPreviews] = useState(true);
  const [categoryIcons, setCategoryIcons] = useState<{ [key: number]: string }>(
    {}
  );
  const [downloadApiLoading, setDownloadApiLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  // Function to fetch category icons
  const fetchCategoryIcons = async () => {
    try {
      const response = await API.get("/master/survey-category-master");
      const iconsMap: { [key: number]: string } = {};

      response?.data?.data?.forEach((category: any) => {
        if (category.category_id && category.category_icon) {
          iconsMap[category.category_id] = `${
            import.meta.env.VITE_API_BASE_URL
          }${category.category_icon}`;
        }
      });

      setCategoryIcons(iconsMap);
    } catch (err) {
      console.log("error fetching master data", err);
    }
  };

  // Fetch category icons on component mount
  useEffect(() => {
    fetchCategoryIcons();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [questionsRes, answersRes] = await Promise.all([
          API.get("/survey/get-questions"),
          API.get("/survey/get-questions-answer"),
        ]);
        setAllQuestionsData(questionsRes?.data?.data);
        setAllAnswersData(answersRes?.data?.data?.survey_submission_questions);
      } catch (err) {
        // Optionally handle error
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch uploaded photos
  useEffect(() => {
    const getAlreadyUploadedPhotos = async () => {
      setLoadingPreviews(true);
      try {
        const response = await API.get("photo-category/get-category-photos");
        if (response?.data?.data) {
          const transformedData = await Promise.all(
            response.data.data.map(async (category: any) => {
              let preview: any = [];
              if (category.uploaded_photos?.length) {
                preview = await Promise.all(
                  category.uploaded_photos.map(async (photo: any) => {
                    const base64 = await API.post(
                      "photo-upload/stream",
                      { filePath: photo.file_path, fileName: photo.file_name },
                      { responseType: "arraybuffer" }
                    )
                      .then((res) =>
                        btoa(
                          new Uint8Array(res.data).reduce(
                            (data, byte) => data + String.fromCharCode(byte),
                            ""
                          )
                        )
                      )
                      .catch(() => null);
                    return {
                      id: photo.id,
                      filePath: photo.file_path,
                      fileName: photo.file_name,
                      previewImg: base64
                        ? `data:image/jpeg;base64,${base64}`
                        : null,
                    };
                  })
                );
              }
              return {
                key: category.id,
                label: category.name,
                uploadLimit: category.max_photos,
                uploaded: category.uploaded_photos?.length > 0,
                preview,
              };
            })
          );
          setPhotos(transformedData);
        }
      } catch (error) {
        console.error("Error fetching photos:", error);
      }
      setLoadingPreviews(false);
    };
    getAlreadyUploadedPhotos();
  }, []);

  const handleOpenPreview = (catIdx: number, imgIdx: number) => {
    const category = photos[catIdx];
    if (category.preview && category.preview.length > 0) {
      const validImages = category.preview
        .map((photo: any) => photo.previewImg)
        .filter((img: any) => img !== null);
      setPreviewImages(validImages);
      setPreviewTitle(category.label);
      setPreviewCatIdx(catIdx);
      setPreviewIdx(imgIdx);
      setPreviewOpen(true);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewIdx(0);
    setPreviewCatIdx(null);
  };

  const handleDownloadPdf = async () => {
    setDownloadApiLoading(true);
    try {
      const response = await API.post(
        "/survey-preview/pdf-download",
        {},
        {
          responseType: "blob",
          headers: {
            Accept: "application/pdf",
          },
        }
      );
      // Check if we received PDF data
      if (response.data.size > 0) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "survey-preview.pdf";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error("Received empty PDF data");
      }
    } catch (err) {
      console.log("error fetching download");
    } finally {
      setDownloadApiLoading(false);
    }
  };

  const notForSchoolTypes = allQuestionsData?.notForSchoolTypes;
  const schoolCategory = getRoleData()?.school_ques_cat;
  const categories = allQuestionsData?.categories || [];
  // Build a flat list of all questions in all categories, in order
  const allQuestionsFlat = categories.reduce((acc: any[], category: any) => {
    const filteredQuestions = getFilteredQuestions(
      category.questions || [],
      notForSchoolTypes,
      schoolCategory
    );
    return [...acc, ...filteredQuestions];
  }, []);
  const questionDisplayNumbers = getQuestionDisplayNumbers(allQuestionsFlat);

  return (
    <>
      <Box
        sx={{
          minHeight: "calc(100vh - 190px)",
          width: "100%",
          background: isDarkMode
            ? "#1A1D21"
            : "linear-gradient(180deg, #DFFCF5 0%, #EEFFF0 100%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          pt: { xs: 2.5, md: 4 },
          pb: 0,
        }}
      >
        <Container
          maxWidth={false}
          sx={{ width: { xs: "100%", md: "88%" }, px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              width: "100%",
              minHeight: { md: "calc(100vh - 260px)" },
              border: `1px solid ${isDarkMode ? "#596268" : "#49DEC8"}`,
              borderRadius: { xs: "22px", md: "34px 34px 0 0" },
              borderBottom: { md: "none" },
              background: isDarkMode
                ? "linear-gradient(180deg, #22282D 0%, #29322F 100%)"
                : "linear-gradient(180deg, rgba(248,252,255,.96) 0%, rgba(235,250,237,.96) 100%)",
              px: { xs: 1.5, sm: 3, md: 5 },
              pt: { xs: 2.5, md: 4 },
              pb: 0,
            }}
          >
            {loading ? (
              <Box
                sx={{
                  minHeight: "80vh",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "transparent",
                }}
              >
                <DataLoader />
              </Box>
            ) : (
              <>
                {categories.map((category: any) => {
                  const filteredQuestions = getFilteredQuestions(
                    category.questions || [],
                    notForSchoolTypes,
                    schoolCategory
                  );
                  // Only show questions that have an answer
                  const answeredQuestions = filteredQuestions.filter(
                    (question: any) => {
                      const answer = allAnswersData?.[question.questionId];
                      if (question.questionType === "multi_input") {
                        return (
                          answer &&
                          typeof answer === "object" &&
                          Object.values(answer).some(
                            (v) => v !== null && v !== ""
                          )
                        );
                      }
                      return (
                        answer !== undefined && answer !== null && answer !== ""
                      );
                    }
                  );
                  if (answeredQuestions.length === 0) return null;

                  const categoryIcon =
                    categoryIcons[category.categoryId] ||
                    iconMap[category.categoryName] ||
                    iconMap["Mission LiFE activities"];

                  return (
                    <Box
                      key={category.categoryId}
                      sx={{
                        mb: { xs: 2.5, md: 4 },
                        backgroundColor: isDarkMode ? "#242B30" : "#FFFFFF",
                        border: `1px solid ${
                          isDarkMode ? "#4E585E" : "#D4D4D4"
                        }`,
                        borderRadius: { xs: "18px", md: "32px" },
                        px: { xs: 2, md: 2.5 },
                        py: { xs: 2, md: 2.5 },
                      }}
                    >
                      {/* Category Header */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          pb: 2,
                          mb: 2,
                          gap: 1.5,
                          borderBottom: `1px solid ${
                            isDarkMode ? "#495158" : "#D4D4D4"
                          }`,
                        }}
                      >
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: "50%",
                            backgroundColor: isDarkMode ? "#343A40" : "#E9E7FF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={categoryIcon}
                            alt={category.categoryName}
                            style={{
                              width: 40,
                              height: 40,
                              filter: isDarkMode
                                ? "invert(1)"
                                : "none",
                            }}
                          />
                        </Box>
                        <Box>
                          <Typography
                            fontWeight={700}
                            sx={{
                              color: isDarkMode ? "#fff" : "#0F202B",
                              fontSize: {
                                xs: `calc(1.15rem * var(--dynamic-font-size, 1))`,
                                md: `calc(1.35rem * var(--dynamic-font-size, 1))`,
                              },
                            }}
                          >
                            {category.categoryName}
                          </Typography>
                          <Typography
                            sx={{
                              color: isDarkMode ? "#B8C0C5" : "#606060",
                              fontSize: {
                                xs: `calc(.86rem * var(--dynamic-font-size, 1))`,
                                md: `calc(.98rem * var(--dynamic-font-size, 1))`,
                              },
                            }}
                          >
                            {answeredQuestions.length} Questions Answered
                          </Typography>
                        </Box>
                      </Box>
                      {/* Questions and Answers */}
                      {answeredQuestions.map((question: any) => {
                        const filteredIndex = filteredQuestions.findIndex(
                          (q) => q.questionId === question.questionId
                        );
                        const questionNumber =
                          questionDisplayNumbers[question.questionId];
                        const answer = allAnswersData[question.questionId];
                        const answerLabel = getAnswerLabel(question, answer);

                        return (
                          <Box
                            key={question.questionId}
                            sx={{
                              py: { xs: 1.5, md: 2 },
                              borderBottom: `1px solid ${
                                isDarkMode ? "#495158" : "#D7D7D7"
                              }`,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: isDarkMode ? "#fff" : "#0F202B",
                                fontSize: {
                                  xs: `calc(0.8rem * var(--dynamic-font-size, 1))`,
                                  sm: `calc(0.9rem * var(--dynamic-font-size, 1))`,
                                  md: `calc(1rem * var(--dynamic-font-size, 1))`,
                                },
                                mb: 1,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{ color: isDarkMode ? "#A7A7A7" : "#1A5F20" }}
                              >
                                {`Q${questionNumber}. `}
                              </Box>
                              {question.questionText}
                            </Typography>
                            <Box
                              sx={{
                                mt: 1.25,
                                px: { xs: 1.5, md: 2 },
                                py: 1.25,
                                borderRadius: "8px",
                                backgroundColor: isDarkMode
                                  ? "#39423F"
                                  : "#E1F7E7",
                              }}
                            >
                              <Typography
                                sx={{
                                  color: isDarkMode ? "#D2D5D3" : "#1A5F20",
                                  fontSize: {
                                    xs: `calc(0.7rem * var(--dynamic-font-size, 1))`,
                                    sm: `calc(0.8rem * var(--dynamic-font-size, 1))`,
                                    md: `calc(0.9rem * var(--dynamic-font-size, 1))`,
                                  },
                                }}
                              >
                                {answerLabel}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
                {/* Photo Preview Section */}
                {photos.some(
                  (cat) => cat.preview && cat.preview.length > 0
                ) && (
                  <Box mb={4}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: "50%",
                          backgroundColor: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: isDarkMode
                            ? "0 0 10px rgba(255,255,255,0.1)"
                            : "0px 1px 4px rgba(0, 0, 0, 0.05)",
                        }}
                      >
                        <img
                          src={iconMap["Mission LiFE activities"]}
                          alt={"Mission LiFE activities"}
                          style={{
                            width: 40,
                            height: 40,
                            filter: isDarkMode
                              ? "brightness(0) invert(1)"
                              : "none",
                          }}
                        />
                      </Box>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                          color: isDarkMode ? "#fff" : "#23235f",
                          fontSize: {
                            xs: `calc(1.1rem * var(--dynamic-font-size, 1))`,
                            sm: `calc(1.2rem * var(--dynamic-font-size, 1))`,
                            md: `calc(1.35rem * var(--dynamic-font-size, 1))`,
                          },
                        }}
                      >
                        {t("previewAndSubmit.uploadedPhotos")}
                      </Typography>
                    </Box>
                    {photos.map((cat, idx) =>
                      cat.preview && cat.preview.length > 0 ? (
                        <Box
                          key={cat.key}
                          mb={2}
                          display="flex"
                          alignItems="center"
                          sx={{
                            background: isDarkMode ? "#23243a" : "#fff",
                            borderRadius: 3,
                            p: 2,
                            boxShadow: isDarkMode
                              ? "0px 1px 4px rgba(255, 255, 255, 0.05)"
                              : "0px 1px 4px rgba(0, 0, 0, 0.05)",
                          }}
                        >
                          {/* Number and Text on the left */}
                          <Box
                            display="flex"
                            alignItems="center"
                            flex={1}
                            gap={2}
                          >
                            <Typography
                              variant="body1"
                              fontWeight="medium"
                              sx={{
                                color: isDarkMode ? "#fff" : "inherit",
                                fontSize: {
                                  xs: "0.75rem",
                                  sm: "0.9375rem",
                                  md: "1rem",
                                },
                                textAlign: "left",
                              }}
                            >
                              {`${idx + 1}. ${cat.label}`}
                            </Typography>
                          </Box>

                          {/* Thumbnail on the right */}
                          <Box position="relative" width={56} height={56}>
                            <Box
                              component="img"
                              src={cat.preview[0]?.previewImg}
                              alt="preview"
                              sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 1,
                                objectFit: "cover",
                                border: `1px solid ${
                                  isDarkMode ? "#424242" : "#e0e0e0"
                                }`,
                                cursor: "pointer",
                                transition: "border-color 0.3s ease",
                                "&:hover": {
                                  borderColor: isDarkMode
                                    ? "#666666"
                                    : "#bdbdbd",
                                },
                              }}
                              onClick={() => handleOpenPreview(idx, 0)}
                            />
                            {cat.preview.length > 1 && (
                              <Box
                                position="absolute"
                                top={0}
                                left={0}
                                width={56}
                                height={56}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                  background: "rgba(44,44,44,0.5)",
                                  color: "#fff",
                                  fontWeight: 700,
                                  fontSize: 18,
                                  borderRadius: 1,
                                  pointerEvents: "none",
                                }}
                              >
                                +{cat.preview.length - 1}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      ) : null
                    )}
                    <ImagePreview
                      open={previewOpen}
                      images={previewImages}
                      currentIndex={previewIdx}
                      title={previewTitle}
                      onClose={handleClosePreview}
                      onPrev={() =>
                        setPreviewIdx((idx) => (idx > 0 ? idx - 1 : idx))
                      }
                      onNext={() =>
                        setPreviewIdx((idx) =>
                          previewImages && idx < previewImages.length - 1
                            ? idx + 1
                            : idx
                        )
                      }
                      hideDelete
                      loading={loadingPreviews}
                    />
                  </Box>
                )}
                {/* Back Button */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    flexWrap: "wrap",
                    mt: { xs: 3, md: 5 },
                    mb: { xs: 3, md: 4 },
                    width: "100%",
                    gap: "10px",
                  }}
                >
                  <Button
                    text={t("previewAndSubmit.downloadPdf")}
                    variant="contained"
                    sx={{
                      maxWidth: "170px",
                      px: 5,
                      height: 48,
                      fontWeight: 600,
                      fontSize: "1rem",
                      borderRadius: "999px !important",
                    }}
                    onClick={() => handleDownloadPdf()}
                    isLoading={downloadApiLoading}
                  />

                  <Button
                    text={t("previewAndSubmit.back")}
                    variant="contained"
                    sx={{
                      maxWidth: "170px",
                      px: 5,
                      height: 48,
                      fontWeight: 600,
                      fontSize: "1rem",
                      borderRadius: "999px !important",
                    }}
                    onClick={() => navigate(-1)}
                  />
                </Box>
                <Box
                  component="footer"
                  sx={{
                    mx: { xs: -1.5, sm: -3, md: -5 },
                    mt: 2,
                    py: 2.5,
                    borderTop: `1px solid ${
                      isDarkMode ? "#465047" : "#D3DDD5"
                    }`,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      color: isDarkMode ? "#AEB4B0" : "#737A76",
                      fontSize: {
                        xs: `calc(.75rem * var(--dynamic-font-size, 1))`,
                        md: `calc(.9rem * var(--dynamic-font-size, 1))`,
                      },
                    }}
                  >
                    © {new Date().getFullYear()} Ministry of Education, Govt. of India. All rights
                    reserved.
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default QuestionPreview;
